/**
 * Monitoring methods — V113-0-7 (ADR-0100 §3; migration 801).
 *
 * Every detector is a method on a configuration item: `(component_id, detector_kind, detector_ref)`
 * with a lifecycle identified → verified → mapped (→ retired). Verification is time-boxed:
 * a member's attestation, CI-run evidence naming the CI, or a collector that read the instance on
 * this run; `expires_at = verified_at + cadence` (the method's cadence, else 90 days, D-113-22 O-3).
 * Past `expires_at` the method reads unverified. Presence is never a pass: nothing here writes a
 * check or a binding. `mapped` is set by the binding writer (Track 1 acceptance / Track 4).
 */
import { generateRandomId } from '../utils/audit-crypto.js';
export const DETECTOR_KINDS = ['connector_check', 'ci_parser', 'aggregate_rule', 'attestation', 'code_library'];
export const METHOD_STATES = ['identified', 'verified', 'mapped', 'retired'];
export const METHOD_FITS = ['native', 'partial', 'substitute'];
export const METHOD_SOURCES = ['repo_scan', 'infra_inventory', 'observed_resource', 'intake_egress', 'collector'];
export const VERIFICATION_SOURCES = ['member', 'ci_run', 'collector'];
/** D-113-22 (O-3): a verification with no cadence lapses after 90 days. */
export const DEFAULT_VERIFICATION_SECONDS = 90 * 86_400;
export class MonitoringMethodError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'MonitoringMethodError';
    }
}
const fail = (code, m) => {
    throw new MonitoringMethodError(code, m);
};
const oneOf = (list, v) => typeof v === 'string' && list.includes(v);
export async function getMethod(db, workspaceId, methodId) {
    const r = await db.query(`SELECT * FROM monitoring_methods WHERE method_id = ? AND workspace_id = ? LIMIT 1`, [methodId, workspaceId]);
    return r.results[0] ?? null;
}
export async function listMethodsForComponent(db, workspaceId, componentId) {
    return (await db.query(`SELECT * FROM monitoring_methods WHERE workspace_id = ? AND component_id = ? ORDER BY detector_kind, detector_ref`, [workspaceId, componentId])).results;
}
/** A method counts as verified only inside its window; "last verified <date>" is `verified_at`. */
export function isMethodVerified(m, now = new Date()) {
    if (m.retired_at || m.state === 'retired' || m.state === 'identified' || !m.verified_at)
        return false;
    return !m.expires_at || Date.parse(m.expires_at) > now.getTime();
}
/**
 * The method row for a detector on a CI: created `identified` on first act, else the existing row
 * (fit / cadence refreshed when given; a retired row is left retired and returned as is).
 */
export async function upsertMethod(db, input) {
    if (!oneOf(DETECTOR_KINDS, input.detector_kind))
        fail('invalid', 'Unknown detector kind.');
    if (!oneOf(METHOD_SOURCES, input.source))
        fail('invalid', 'Unknown method source.');
    const ref = (input.detector_ref ?? '').trim();
    if (!ref || ref.length > 200)
        fail('invalid', 'detector_ref is required (at most 200 characters).');
    const fit = input.fit ?? 'native';
    if (!oneOf(METHOD_FITS, fit))
        fail('invalid', 'fit must be native, partial or substitute.');
    const cadence = input.cadence_seconds == null ? null : Math.max(0, Math.floor(Number(input.cadence_seconds))) || null;
    const ci = (await db.query(`SELECT 1 AS ok FROM ai_components WHERE component_id = ? AND workspace_id = ? LIMIT 1`, [input.component_id, input.workspace_id])).results[0];
    if (!ci)
        fail('not_found', 'Configuration item not found.');
    const nowIso = (input.now ?? new Date()).toISOString();
    const existing = (await db.query(`SELECT * FROM monitoring_methods WHERE workspace_id = ? AND component_id = ? AND detector_kind = ? AND detector_ref = ? LIMIT 1`, [input.workspace_id, input.component_id, input.detector_kind, ref])).results[0];
    if (existing) {
        if (!existing.retired_at && (input.fit !== undefined || cadence !== null)) {
            await db.execute(`UPDATE monitoring_methods SET fit = ?, cadence_seconds = COALESCE(?, cadence_seconds), updated_at = ? WHERE method_id = ? AND workspace_id = ?`, [input.fit ?? existing.fit, cadence, nowIso, existing.method_id, input.workspace_id]);
            return (await getMethod(db, input.workspace_id, existing.method_id));
        }
        return existing;
    }
    const method_id = (input.newId ?? generateRandomId)('mth');
    await db.execute(`INSERT INTO monitoring_methods (method_id, workspace_id, component_id, detector_kind, detector_ref, state, fit, source, cadence_seconds, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, 'identified', ?, ?, ?, ?, ?)`, [method_id, input.workspace_id, input.component_id, input.detector_kind, ref, fit, input.source, cadence, nowIso, nowIso]);
    return (await getMethod(db, input.workspace_id, method_id));
}
/** Stamp a verification: `identified` → `verified` (a `mapped` method stays mapped); a retired method is refused. */
export async function verifyMethod(db, input) {
    if (!oneOf(VERIFICATION_SOURCES, input.verification_source))
        fail('invalid', 'Unknown verification source.');
    const by = (input.verified_by ?? '').trim();
    if (!by)
        fail('invalid', 'verified_by is required.');
    const m = await getMethod(db, input.workspace_id, input.method_id) ?? fail('not_found', 'Monitoring method not found.');
    if (m.retired_at || m.state === 'retired')
        fail('conflict', 'A retired method cannot be verified.');
    const now = input.now ?? new Date();
    const cadence = input.cadence_seconds ?? m.cadence_seconds ?? DEFAULT_VERIFICATION_SECONDS;
    const seconds = cadence > 0 ? cadence : DEFAULT_VERIFICATION_SECONDS;
    const expires = new Date(now.getTime() + seconds * 1000).toISOString();
    const nowIso = now.toISOString();
    await db.execute(`UPDATE monitoring_methods SET state = CASE WHEN state = 'mapped' THEN 'mapped' ELSE 'verified' END,
            verified_at = ?, verified_by = ?, verification_source = ?, expires_at = ?, cadence_seconds = COALESCE(?, cadence_seconds), updated_at = ?
      WHERE method_id = ? AND workspace_id = ?`, [nowIso, by, input.verification_source, expires, input.cadence_seconds ?? null, nowIso, m.method_id, input.workspace_id]);
    return (await getMethod(db, input.workspace_id, m.method_id));
}
/**
 * A collector read the instance's state on this run: its `connector_check:<check_key>` method on
 * the CI is created on first sight and re-stamped `verified` (source `collector`) on every run.
 */
export async function recordCollectorVerification(db, input) {
    const m = await upsertMethod(db, {
        workspace_id: input.workspace_id, component_id: input.component_id, detector_kind: 'connector_check', detector_ref: input.check_key,
        source: 'collector', fit: 'native', cadence_seconds: input.cadence_seconds ?? null, now: input.now, newId: input.newId,
    });
    if (m.retired_at)
        return m;
    return verifyMethod(db, { workspace_id: input.workspace_id, method_id: m.method_id, verified_by: input.connector_id, verification_source: 'collector', cadence_seconds: input.cadence_seconds ?? null, now: input.now });
}
/**
 * CI-run evidence that names the CI (`ci_tag` or `component_key` in the structured evidence) verifies
 * the `ci_parser:<source_format>` method on it automatically (`verification_source = 'ci_run'`,
 * `verified_by` = the CI token). The result says WHY nothing verified (V114-1-7c): an unknown name
 * never guesses, and a resolved CI whose method is retired is `no_matching_method`, not a verification.
 */
export async function resolveCiRunVerification(db, input) {
    const tag = (input.ci.ci_tag ?? '').trim();
    const key = (input.ci.component_key ?? '').trim();
    if (!tag && !key)
        return { outcome: 'unknown_configuration_item' };
    const ci = (await db.query(`SELECT component_id FROM ai_components WHERE workspace_id = ? AND merged_into_id IS NULL AND retired_at IS NULL AND ((? <> '' AND ci_tag = ?) OR (? <> '' AND component_key = ?)) LIMIT 1`, [input.workspace_id, tag, tag, key, key])).results[0];
    if (!ci)
        return { outcome: 'unknown_configuration_item' };
    const m = await upsertMethod(db, {
        workspace_id: input.workspace_id, component_id: ci.component_id, detector_kind: 'ci_parser', detector_ref: input.source_format,
        source: 'repo_scan', fit: 'native', cadence_seconds: input.cadence_seconds ?? null, now: input.now, newId: input.newId,
    });
    if (m.retired_at)
        return { outcome: 'no_matching_method', component_id: ci.component_id };
    const method = await verifyMethod(db, { workspace_id: input.workspace_id, method_id: m.method_id, verified_by: input.token_id, verification_source: 'ci_run', cadence_seconds: input.cadence_seconds ?? null, now: input.now });
    return { outcome: 'verified', method };
}
/** The verified method, or null (see `resolveCiRunVerification` for the reason split, V114-1-7c). */
export async function verifyMethodFromCiRun(db, input) {
    const r = await resolveCiRunVerification(db, input);
    return r.outcome === 'verified' ? r.method : null;
}
/** The binding writer's hook (Track 1 accept / Track 4): mapped ⇄ verified; a never-verified method returns to identified. */
export async function setMethodMapped(db, input) {
    const m = await getMethod(db, input.workspace_id, input.method_id) ?? fail('not_found', 'Monitoring method not found.');
    if (m.retired_at || m.state === 'retired')
        fail('conflict', 'A retired method cannot be mapped.');
    const state = input.mapped ? 'mapped' : m.verified_at ? 'verified' : 'identified';
    await db.execute(`UPDATE monitoring_methods SET state = ?, updated_at = ? WHERE method_id = ? AND workspace_id = ?`, [state, (input.now ?? new Date()).toISOString(), m.method_id, input.workspace_id]);
    return (await getMethod(db, input.workspace_id, m.method_id));
}
/**
 * Expiry sweep (cron): a `verified` method past `expires_at` returns to `identified` (its
 * `verified_at` stays as "last verified"); a `mapped` one keeps its state and reads unverified
 * through `isMethodVerified` until re-stamped.
 */
export async function sweepExpiredMethodVerifications(db, now = new Date()) {
    const nowIso = now.toISOString();
    const r = await db.execute(`UPDATE monitoring_methods SET state = 'identified', updated_at = ? WHERE state = 'verified' AND retired_at IS NULL AND expires_at IS NOT NULL AND expires_at <= ?`, [nowIso, nowIso]);
    return { expired: r.meta?.changes ?? 0 };
}
export async function retireMethodsForComponent(db, workspaceId, componentId, now = new Date()) {
    const nowIso = now.toISOString();
    const r = await db.execute(`UPDATE monitoring_methods SET state = 'retired', retired_at = ?, updated_at = ? WHERE workspace_id = ? AND component_id = ? AND retired_at IS NULL`, [nowIso, nowIso, workspaceId, componentId]);
    return r.meta?.changes ?? 0;
}
//# sourceMappingURL=monitoring-method-service.js.map